<script setup lang="ts">
/**
 * One bar split into four parts of a period: busy, waiting on you, waiting on another teammate or system, and idle.
 * Each part is as wide as its share of the time, whatever unit the times are in.
 *
 * Colour never carries the meaning alone (BG §22.3): each part takes an existing status tone (running, action,
 * neutral, muted), and the bar carries a label that states every part in words for screen readers. A part with no
 * time is not drawn.
 */
import { computed } from 'vue'
import { TIME_SPLIT_PARTS as PARTS, type TimeSplitPartKey } from '../lib/time-split'

const props = defineProps<{
    busy: number
    waitingOnYou: number
    waitingOnOther: number
    idle: number
    /** Words for screen readers in place of the shares, such as each part's duration. */
    label?: string
}>()

const amount = (key: TimeSplitPartKey): number => Math.max(0, Number(props[key]) || 0)

const total = computed(() => PARTS.reduce((sum, part) => sum + amount(part.key), 0))

const drawn = computed(() =>
    total.value <= 0
        ? []
        : PARTS.filter(part => amount(part.key) > 0).map(part => ({
              ...part,
              share: (amount(part.key) / total.value) * 100,
          })),
)

const ariaLabel = computed(() => {
    if (props.label) return props.label
    const shares = PARTS.map(part => {
        const share = total.value > 0 ? Math.round((amount(part.key) / total.value) * 100) : 0
        return `${part.words} ${share}%`
    })
    const sentence = shares.join(', ')
    return sentence.charAt(0).toUpperCase() + sentence.slice(1)
})
</script>

<template>
    <span class="zh-time-split" role="img" :aria-label="ariaLabel">
        <span
            v-for="part in drawn"
            :key="part.key"
            class="zh-time-split__part"
            :data-part="part.key"
            :data-tone="part.tone"
            :style="{ width: `${part.share}%`, '--zh-time-split-colour': part.colour }"
        />
    </span>
</template>

<style scoped>
.zh-time-split {
    display: flex;
    width: 100%;
    min-width: 6rem;
    height: 0.5rem;
    overflow: hidden;
    border: 1px solid var(--border);
    background: var(--muted);
}

/* Each part's colour is its `colour` in `TIME_SPLIT_PARTS`, bound per part, the one definition the legend shares. */
.zh-time-split__part {
    display: block;
    height: 100%;
    background: var(--zh-time-split-colour);
}
</style>
